<section>
  @if (query.isPending()) {
  <section class="h-screen w-screen overflow-hidden p-16">
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
  </section>

  } @else if (query.isError()) {
  <section
    class="h-screen w-screen overflow-hidden p-16 flex items-center pt-32 justify-center my-autox"
  >
    <nz-result
      nzStatus="500"
      nzTitle="500"
      nzSubTitle="The activation link might be invalid or expired"
    >
      <div nz-result-extra>
        <div>
          If the problem persists, kindly contact technical support for
          assistance.
        </div>
        <div class="flex items-center my-2 justify-center">
          <button class="btn btn-outline btn-primary" routerLink="/passport/login">
            Go to Login
          </button>
        </div>
      </div>
    </nz-result>
  </section>

  } @else {
  <section
    *ngIf="query.isSuccess()"
    class="grid grid-cols-1 lg:grid-cols-2 h-screen bg-gradient-to-br from-slate-100 via-white to-indigo-500"
  >
    <div
      class="w-full px-4 py-12 mx-auto bg-whitex xl:py-24 md:w-3/5 lg:w-4/5 xl:w-3/5"
    >
      <div *ngIf="query.isFetched; else accountSetupAlreadyCompleted">
        <div *ngIf="!submitted; else resultTpl">
          <a [href]="websiteUrl">
            <div class="w-56 cursor-pointer mb-8">
              <img
                alt="BusPro Logo"
                ngSrc="logo-no-border.png"
                height="188"
                width="600"
                priority
              />
            </div>
          </a>

          <h1
            class="mb-4 -mt-3 text-2xl font-extrabold leading-snug tracking-tight text-left text-gray-900 md:text-4xl"
          >
            Complete Your Admin Account Setup
          </h1>

          <!-- Error Display -->
          <div class="mb-6" *ngIf="showError()">
            <app-error-display
              [error]="errorDetails"
              [showRetry]="true"
              (retry)="onRetry()"
              (dismiss)="onDismissError()"
            />
          </div>

          <!-- Email Verification Step -->
          @if (emailVerificationStep() === 'verifying') {
          <div class="text-center py-8 bg-blue-50 rounded-lg">
            <nz-spin nzSimple [nzSize]="'large'"></nz-spin>
            <p class="mt-4 text-lg text-gray-700">
              Verifying your email address...
            </p>
          </div>
          }

          @if (emailVerificationStep() === 'verified') {
            <nz-alert
              nzType="success"
              nzMessage="Email Verified!"
              nzDescription="Your email address has been verified. Please set your password below."
              [nzCloseable]="false"
              class="mb-6"
            ></nz-alert>

            <nz-alert
              *ngIf="user$ | async as user"
              [nzMessage]="'Welcome ' + user.firstName + ' ' + user.surname"
              [nzDescription]="
                'Please set a strong password to activate your BusPro admin account.'
              "
              nzType="info"
              class="mb-6"
            ></nz-alert>

            <!-- Password Requirements Alert -->
            <nz-alert
              [nzType]="passwordRequirements.enforced ? 'warning' : 'info'"
              class="mb-6"
            >
              <div *nzDescription>
                <p class="font-semibold mb-2">
                  {{ passwordRequirements.enforced ? 'Password Requirements:' : 'Password Validation Relaxed for Testing' }}
                </p>
                @if (passwordRequirements.enforced) {
                  <ul class="list-disc list-inside text-sm space-y-1">
                    <li>Minimum {{ passwordRequirements.minLength }} characters</li>
                    <li>At least one uppercase letter</li>
                    <li>At least one lowercase letter</li>
                    <li>At least one number</li>
                    <li>At least one special character (&#64;$!%*?&)</li>
                  </ul>
                } @else {
                  <p class="text-sm">
                    Minimum {{ passwordRequirements.minLength }} characters only. No complexity requirements.
                    <br />
                    <strong>Note:</strong> This is for development/testing only. Production will enforce strict rules.
                  </p>
                }
              </div>
            </nz-alert>

            <form
              #accountForm
              (ngSubmit)="completeAccountSetup()"
              [formGroup]="form"
              class="mt-8 space-y-4"
              novalidate
            >
            <label class="block">
              <span class="block mb-1 text-sm font-medium text-gray-700"
                >Email Address</span
              >
              <input
                class="form-input w-full bg-gray-100"
                autocomplete="username"
                formControlName="email"
                inputmode="email"
                name="email"
                nz-input
                nzSize="large"
                type="email"
                readonly
              />
            </label>

            <label class="block">
              <span class="block mb-1 text-sm font-medium text-gray-700"
                >Password</span
              >
              <input
                class="form-input w-full"
                autocomplete="new-password"
                formControlName="password"
                name="password"
                nz-input
                nzSize="large"
                type="password"
                required
                [placeholder]="passwordRequirements.enforced ? 'Enter a strong password' : 'Enter your password'"
              />
              
              <!-- Password Strength Indicator -->
              @if (form.get('password')?.value && passwordRequirements.enforced) {
                <div class="mt-2">
                  <div class="flex items-center gap-2">
                    <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        class="h-full transition-all duration-300"
                        [ngClass]="{
                          'bg-red-500': passwordStrength() < 40,
                          'bg-orange-500': passwordStrength() >= 40 && passwordStrength() < 60,
                          'bg-yellow-500': passwordStrength() >= 60 && passwordStrength() < 80,
                          'bg-green-500': passwordStrength() >= 80
                        }"
                        [style.width.%]="passwordStrength()"
                      ></div>
                    </div>
                    <span
                      class="text-xs font-medium"
                      [ngClass]="getPasswordStrengthClass()"
                    >
                      {{ getPasswordStrengthLabel() }}
                    </span>
                  </div>
                  @if (passwordFeedback()) {
                    <p class="text-xs text-gray-600 mt-1">{{ passwordFeedback() }}</p>
                  }
                </div>
              }
            </label>

            <label class="block">
              <span class="block mb-1 text-sm font-medium text-gray-700"
                >Confirm Password</span
              >
              <input
                class="form-input w-full"
                autocomplete="new-password"
                formControlName="passwordConfirm"
                name="passwordConfirm"
                nz-input
                nzSize="large"
                type="password"
                required
                placeholder="Re-enter your password"
              />
            </label>

            <div>
              <button
                [disabled]="isSubmitting()"
                class="w-full mt-8 btn btn-primary btn-lg"
                type="submit"
              >
                @if (isSubmitting()) {
                <span class="loading loading-spinner"></span>
                Completing Setup...
                } @else {
                Complete Account Setup
                }
                </button>
              </div>
            </form>

            <div
              class="pt-6 mt-6 text-sm font-medium text-gray-700 border-t border-gray-200"
            >
              Already have an account?
              <a class="text-indigo-700 hover:text-indigo-900" routerLink="/passport/login">
                Login
              </a>
            </div>
          }
        </div>

        <ng-template #resultTpl>
          <nz-result
            nzStatus="success"
            nzTitle="Account Setup Complete!"
            nzSubTitle="Your BusPro admin account has been successfully activated. You can now login to access the admin portal."
          >
            <div nz-result-extra>
              <button class="btn btn-primary" routerLink="/passport/login">
                Sign In to Admin Portal
              </button>
            </div>
          </nz-result>
        </ng-template>
      </div>

      <ng-template #accountSetupAlreadyCompleted>
        <nz-result
          nzStatus="info"
          nzTitle="Account Already Activated"
          nzSubTitle="Your account setup has already been completed. Please sign in to continue."
        >
          <div nz-result-extra>
            <button class="btn btn-primary" routerLink="/passport/login">
              Sign In
            </button>
            <button
              class="btn btn-ghost"
              routerLink="/passport/forgot-password"
            >
              Reset Password
            </button>
          </div>
        </nz-result>
      </ng-template>

      <div class="mt-8 text-gray-400 text-center text-sm">
        Copyright &nbsp; &#64;{{ date | date : "YYYY" }}. BusPro. All rights
        reserved.
      </div>
    </div>

    <div
      class="px-4 py-20 space-y-10 bg-gray-100 xl:py-32 md:px-40 lg:px-20 xl:px-40 bg-cover"
      style="background-image: url('theme3.jpg')"
    >
      <div class="max-w-md mx-auto text-center text-white">
        <h2 class="text-3xl font-bold mb-4">BusPro Admin Portal</h2>
        <p class="text-lg opacity-90">
          Manage your platform, tenants, and operations securely.
        </p>
      </div>
    </div>
  </section>
  }
</section>

<ng-template #pageError>
  <section
    class="h-screen w-screen overflow-hidden p-16 flex items-center pt-32 justify-center"
  >
    <nz-result
      nzStatus="500"
      nzTitle="Activation Error"
      [nzSubTitle]="activationErrorMessage()"
    >
      <div nz-result-extra>
        <button class="btn btn-primary" routerLink="/passport/login">
          Go to Login
        </button>
        <button class="btn btn-ghost" (click)="retryActivation()">
          Retry
        </button>
      </div>
    </nz-result>
  </section>
</ng-template>
